import {computed, effect, runInInjectionContext, Signal, signal, WritableSignal} from '@angular/core';
import {MainInjector} from '@shared-types';

/* ----------------------------------------------  Validations ---------------------------------------------- */
export type SignalValidationType = 'required' | 'minLength' | 'maxLength' | 'pattern' | 'email';
export type SignalValidationStruct = (
	| {
			type: Exclude<SignalValidationType, 'required' | 'email'>;
			value: any;
	  }
	| {
			type: Exclude<SignalValidationType, 'minLength' | 'maxLength' | 'pattern'>;
	  }
) & {errorMessage?: string};

/* -------------------------------------------------------------------------------------------------------------- */
export type SignalBasedBinderInput<T extends object> = [model: WritableSignal<T> | SignalBinderModel<T>, prop: keyof T, validations?: (SignalValidationType | SignalValidationStruct)[]];
/* -------------------------------------------------------------------------------------------------------------- */
export class SignalBinderModel<T extends object = any> {
	public signal$: WritableSignal<T>;
	public select: (prop: keyof T) => T[keyof T] = (prop: keyof T) => this.signal$()[prop];

	constructor(
		ModelConstructor: new (...args: any[]) => T,
		constructorArgs?: ConstructorParameters<typeof ModelConstructor>,
		private isValidDefault = false,
		private signalMirror?: WritableSignal<T>,
	) {
		const model = new ModelConstructor(...constructorArgs);
		this.signal$ = signal(model);
		if (this.signalMirror) {
			this.signal$.set(this.signalMirror());
			runInInjectionContext(MainInjector, () => {
				effect(() => {
					const s = this.signal$();
					this.signalMirror.set(s);
				});
			});
		}
	}

	/* ----------------------------------------------- Validators ----------------------------------------------- */
	public validators: WritableSignal<Record<keyof T, boolean>> = signal({} as Record<keyof T, boolean>);

	public isValid: Signal<boolean> = computed(() => {
		const validators = Object.values(this.validators());
		return validators.length > 0 ? validators.every(Boolean) : this.isValidDefault;
	});

	public setPropValidity(prop: keyof T, isValid: boolean = this.isValidDefault): void {
		this.validators.set({...this.validators(), [prop]: isValid});
	}

	public patch(param: Partial<T>): void {
		this.signal$.update(current => ({...current, ...param}));
	}

	public set(value: T): void {
		this.signal$.set(value);
	}
}
